import React, { useState, useEffect } from 'react'
import { get as lGet } from 'lodash'
import {
  apiCreateCompany,
  apiGetCompanyFromId,
  apiUpdateCompanyDetails
} from '../../../../utils/api'
import { CircularProgress } from '@mui/material'

import { IsShortNameValid } from '../utils'

import ModalTitleLabel from '../../../atoms/ModalTitleLabel'
import InputForm from '../../../molecules/InputForm'
import ErrorMessage from '../../../atoms/ErrorMessage'
import ModalButtonsSet from '../../../molecules/ModalButtonsSet'
import { ERROR_MESSAGE_OF_DUPLICATE_COMPANY_CODE,
   ERROR_MESSAGE_OF_DUPLICATE_COMPANY_NAME } from './constants'

const EditCompanyModal = (props) => {
  const { targetId, closeEditModal } = props

  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false)
  const [isInProgress, setIsInProgress] = useState(false)

  // ★ 追加：APIキー表示用
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const [issuedApiKey, setIssuedApiKey] = useState('')
  const [issuedApiKeyPrefix, setIssuedApiKeyPrefix] = useState('')
  const [copied, setCopied] = useState(false)

  const isNameEmpty = !name || !shortName
  const disabled = isNameEmpty
  const isEditMode = !!targetId
  const modalTitle = isEditMode ? 'title_edit_company' : 'title_create_company'

  const fetchCompanyFromId = async () => {
    const {
      data: { data: companyDetails },
    } = await apiGetCompanyFromId(targetId)
    const { name, short_name } = companyDetails[0]
    setName(name)
    setShortName(short_name)
  }

  const createCompanySubmit = async () => {
    setIsInProgress(true)
    setErrorMessage('')

    try {
      const res = await apiCreateCompany({
        name: name,
        short_name: shortName,
      })

      // ★ ここが肝：作成レスポンスから平文APIキーを取り出す
      const payload = lGet(res, 'data.data', null)
      const apiKey = payload?.api_key || ''
      const apiKeyPrefix = payload?.api_key_prefix || ''

      if (apiKey) {
        setIssuedApiKey(apiKey)
        setIssuedApiKeyPrefix(apiKeyPrefix)
        setCopied(false)
        setShowApiKeyModal(true)
        // ★ ここで reload しない（キー表示が消えるから）
      } else {
        // もしバックエンドが api_key を返さないなら従来通りリロード
        window.location.reload()
      }
    } catch (e) {
      const responseMessage = lGet(e, 'response.data.message')
      if (responseMessage === 'RECAPTCHA_ERROR') {
        setErrorMessage('Error occurred validating captcha. Please try again')
      } else if (responseMessage === ERROR_MESSAGE_OF_DUPLICATE_COMPANY_CODE) {
        setErrorMessage('company_code_duplicated')
      } else if (responseMessage === ERROR_MESSAGE_OF_DUPLICATE_COMPANY_NAME) {
        setErrorMessage('company_name_duplicated')
      } else {
        setErrorMessage('unknown_error')
      }
      setRefreshReCaptcha(!refreshReCaptcha)
    }

    setIsInProgress(false)
  }

  const editCompanySubmit = async (targetId) => {
    setIsInProgress(true)
    setErrorMessage('')

    try {
      await apiUpdateCompanyDetails({
        name: name,
        short_name: shortName,
        id: targetId,
      })
      window.location.reload()
    } catch (e) {
      const responseMessage = lGet(e, 'response.data.message')
      if (responseMessage === 'RECAPTCHA_ERROR') {
        setErrorMessage('Error occurred validating captcha. Please try again')
      } else if (responseMessage === ERROR_MESSAGE_OF_DUPLICATE_COMPANY_CODE) {
        setErrorMessage('company_code_duplicated')
      } else {
        setErrorMessage('unknown_error')
      }
      setRefreshReCaptcha(!refreshReCaptcha)
    }

    setIsInProgress(false)
  }

  const returnIsError = () => {
    const isShortNameCorrect = IsShortNameValid(shortName)
    if (!!shortName && !isShortNameCorrect) {
      setErrorMessage('company_short_name_validation_error')
      return true
    } else {
      return false
    }
  }

  const handleRegisterCompany = async () => {
    const isError = await returnIsError()
    if (!isError) {
      if (isEditMode) {
        editCompanySubmit(targetId)
      } else {
        createCompanySubmit()
      }
    }
  }

  // ★ 追加：APIキーコピー処理
  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(issuedApiKey)
      setCopied(true)
    } catch (e) {
      // clipboard APIが使えない環境向けのフォールバック
      try {
        const ta = document.createElement('textarea')
        ta.value = issuedApiKey
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        setCopied(true)
      } catch (e2) {
        setCopied(false)
      }
    }
  }

  // ★ 追加：APIキー表示モーダルを閉じる処理
  // 事故防止のため「コピーしてから閉じる」方針にする（必要なら緩められる）
  const closeApiKeyModal = () => {
    if (!copied) return
    setShowApiKeyModal(false)
    // キー表示後に一覧更新
    window.location.reload()
  }

  useEffect(() => {
    if (isEditMode) fetchCompanyFromId()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ===== ここから描画 =====
  return (
    <div className='modal-wrapper'>
      <div
        className='modal-contents'
        onClick={e => { e.stopPropagation() }}
        style={{
          position: 'relative',
          opacity: isInProgress ? '.8' : '1'
        }}
      >
        <ModalTitleLabel i18nKey={modalTitle} />
        <div className='flex'>
          <div className='input-wrapper half-width mr-4'>
            <InputForm
              i18nKey='company_name'
              required={true}
              type='text'
              value={name}
              setValue={setName}
              autoComplete='register name'
            />
          </div>
          <div className='ml-4 input-wrapper half-width'>
            <InputForm
              i18nKey='company_short_name'
              required={true}
              type='text'
              value={shortName}
              setValue={setShortName}
              autoComplete='register short_name'
              disabled={isEditMode ? true : false}
            />
          </div>

          {isInProgress && (
            <div>
              <CircularProgress
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%'
                }}
              />
            </div>
          )}
        </div>

        <div className='flex'>
          <div className='input-wrapper mr-4'>
            <ErrorMessage i18nKey={errorMessage} />
          </div>
        </div>

        <ModalButtonsSet
          confirmButtonI18nKey='register'
          submitHandler={handleRegisterCompany}
          cancelHandler={closeEditModal}
          disabled={disabled}
        />
      </div>

      {/* ★ 追加：APIキー表示モーダル */}
      {showApiKeyModal && (
        <div className="modal-wrapper">
          <div
            className="modal-contents"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative" }}
          >
            <ModalTitleLabel i18nKey="title_api_key_issued" />
            <div style={{ marginTop: 12 }}>
              <div style={{ color: "#b71c1c", fontWeight: "bold", marginBottom: 8 }}>
                ※ APIキーは再表示できません。必ずコピーして保管してください。
              </div>

              {issuedApiKeyPrefix && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>API Key Prefix</div>
                  <div style={{ fontFamily: "monospace" }}>{issuedApiKeyPrefix}</div>
                </div>
              )}

              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.8 }}>API Key（平文）</div>
                <div
                  style={{
                    padding: 12,
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    background: "#fafafa",
                  }}
                >
                  {issuedApiKey}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button className="btn" onClick={handleCopyApiKey}>
                  コピー
                </button>
                <button
                  className="btn"
                  onClick={closeApiKeyModal}
                  disabled={!copied}
                  title={!copied ? "コピー後に閉じられます" : ""}
                >
                  閉じる
                </button>
              </div>

              {copied && (
                <div style={{ marginTop: 8, color: "green" }}>
                  コピーしました ✅
                </div>
              )}
              {!copied && (
                <div style={{ marginTop: 8, opacity: 0.8 }}>
                  閉じるには「コピー」を押してください
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditCompanyModal